import { useState, useCallback, useEffect } from "react";

const NOTIFICATION_TYPES = {
  ENQUIRIES: 'enquiries',
  SELECTIONS: 'selections',
  JOB_APPLICATIONS: 'jobApplications',
  EVENT_ENROLLMENTS: 'eventEnrollments'
};

export const useHr = (userId, userRole) => {
  const [notifications, setNotifications] = useState({
    enquiries: { data: [], count: 0, loading: false },
    selections: { data: [], count: 0, loading: false },
    jobApplications: { data: [], count: 0, loading: false },
    eventEnrollments: { data: [], count: 0, loading: false }
  });
  
  const API_URL = process.env.NEXT_PUBLIC_API_URL;

  const getUnreadEntryWeight = useCallback((notification) => {
    if (!notification || notification.isRead) return 0;
    if (Number.isFinite(notification.pendingCount) && notification.pendingCount > 0) {
      return notification.pendingCount;
    }
    return 1;
  }, []);

  const updateNotificationState = useCallback((type, updates) => {
    setNotifications(prev => ({
      ...prev,
      [type]: { ...prev[type], ...updates }
    }));
  }, []);

  const fetchEnquiryNotifications = useCallback(async () => {
    if (!userId || !userRole) return;
    
    updateNotificationState('enquiries', { loading: true });
    try {
      const response = await fetch(
        `${API_URL}/enquiries/recent-unviewed`,
        { method: "GET", credentials: "include" }
      );
      
      if (response.ok) {
        const result = await response.json();
        updateNotificationState('enquiries', {
          data: result.data || [],
          count: result.count || 0,
          loading: false
        });
      } else {
        updateNotificationState('enquiries', { data: [], count: 0, loading: false });
      }
    } catch (error) {
      console.error("Error fetching enquiry notifications:", error);
      updateNotificationState('enquiries', { data: [], count: 0, loading: false });
    }
  }, [userId, userRole, API_URL, updateNotificationState]);

  const fetchTypedNotifications = useCallback(async (type, apiType) => {
    if (!userId || userRole !== 'hr') return;
    
    updateNotificationState(type, { loading: true });
    try {
      const response = await fetch(
        `${API_URL}/notifications?type=${apiType}&limit=50&page=1`,
        { method: "GET", credentials: "include" }
      );
      
      if (response.ok) {
        const result = await response.json();
        const data = result.data || [];
        const unreadCount = data.reduce((sum, n) => sum + getUnreadEntryWeight(n), 0);
        
        updateNotificationState(type, {
          data,
          count: unreadCount,
          loading: false
        });
      } else {
        updateNotificationState(type, { data: [], count: 0, loading: false });
      }
    } catch (error) {
      console.error(`Error fetching ${type} notifications:`, error);
      updateNotificationState(type, { data: [], count: 0, loading: false });
    }
  }, [userId, userRole, API_URL, updateNotificationState, getUnreadEntryWeight]);

  const markEnquiriesAsViewed = useCallback(async () => {
    if (!userId || !userRole) return;
    
    try {
      const response = await fetch(
        `${API_URL}/enquiries/${userId}/mark-all-viewed`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ userType: userRole }),
        }
      );
      
      if (response.ok) {
        updateNotificationState('enquiries', { data: [], count: 0 });
      }
    } catch (error) {
      console.error("Error marking enquiries as viewed:", error);
    }
  }, [userId, userRole, API_URL, updateNotificationState]);

  const markNotificationAsRead = useCallback(async (notificationId, type) => {
    if (!notificationId || !userId) return;
    
    try {
      const response = await fetch(
        `${API_URL}/notifications/${notificationId}/read`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        }
      );
      
      if (response.ok) {
        const previousNotification = notifications[type]?.data?.find(n => n._id === notificationId);
        const previousWeight = getUnreadEntryWeight(previousNotification);

        setNotifications(prev => ({
          ...prev,
          [type]: {
            ...prev[type],
            data: prev[type].data.map(n => 
              n._id === notificationId ? { ...n, isRead: true } : n
            ),
            count: Math.max(0, prev[type].count - previousWeight)
          }
        }));
      }
    } catch (error) {
      console.error(`Error marking ${type} notification as read:`, error);
    }
  }, [userId, API_URL, notifications, getUnreadEntryWeight]);

  useEffect(() => {
    if (userId && userRole) {
      fetchEnquiryNotifications();
      if (userRole === 'hr') {
        fetchTypedNotifications('selections', 'APPROVAL_BY_STUDENT,REJECTION_BY_STUDENT');
        fetchTypedNotifications('jobApplications', 'JOB_APPLICATION');
        fetchTypedNotifications('eventEnrollments', 'EVENT_ENROLLMENT');
      }
    }
  }, [userId, userRole, fetchEnquiryNotifications, fetchTypedNotifications]);

  const totalCount = Object.values(notifications).reduce((sum, n) => sum + n.count, 0);

  return {
    notifications,
    totalCount,
    fetchEnquiryNotifications,
    fetchSelectionNotifications: () => fetchTypedNotifications('selections', 'APPROVAL_BY_STUDENT,REJECTION_BY_STUDENT'),
    fetchJobApplicationNotifications: () => fetchTypedNotifications('jobApplications', 'JOB_APPLICATION'),
    fetchEventEnrollmentNotifications: () => fetchTypedNotifications('eventEnrollments', 'EVENT_ENROLLMENT'),
    markEnquiriesAsViewed,
    markNotificationAsRead,
  };
};
